/**
 * Notification click capture.
 *
 * Every push carries a click token (`nt`) — the id of the notificationLedger row
 * for that exact (uid, send). We echo it back on tap so the click lands on that
 * row, which gives us response rates per send and surfaces the case where the uid
 * we sent to isn't the uid that tapped (a stale account).
 *
 * Tokens are stashed in localStorage BEFORE being sent, then flushed once a uid
 * exists — the same shape as attributionService, and for the same reason: a tap
 * that cold-starts the app fires its handler before auth resolves. The old
 * open-event write drops those on the floor (`if (uid)`), so cold-start taps are
 * undercounted today. Stashing first means the click survives the gap.
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

const STASH_KEY = 'enough_pending_notification_clicks';
/** A tap we couldn't attribute within a few days is not worth carrying forever. */
const MAX_STASH_AGE_MS = 7 * 24 * 60 * 60 * 1000;
/** Guards against an unbounded queue if the callable is failing persistently. */
const MAX_STASHED = 20;

interface PendingClick {
    token: string;
    stashedAt: number;
}

interface RecordClickResponse {
    recorded: boolean;
    reason?: string;
    uidMismatch?: boolean;
}

function readStash(): PendingClick[] {
    try {
        const raw = localStorage.getItem(STASH_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        const cutoff = Date.now() - MAX_STASH_AGE_MS;
        return parsed.filter(
            (c): c is PendingClick =>
                !!c && typeof c.token === 'string' && typeof c.stashedAt === 'number' && c.stashedAt > cutoff,
        );
    } catch {
        return [];
    }
}

function writeStash(clicks: PendingClick[]): void {
    try {
        if (clicks.length === 0) {
            localStorage.removeItem(STASH_KEY);
            return;
        }
        localStorage.setItem(STASH_KEY, JSON.stringify(clicks.slice(-MAX_STASHED)));
    } catch {
        // Private mode / quota. A dropped click is not worth breaking navigation over.
    }
}

/**
 * Stash a click token. Safe to call before auth resolves — this only touches
 * localStorage. Call `flushNotificationClicks` once a uid exists.
 */
export const stashNotificationClick = (token: string | undefined | null): void => {
    if (!token) return;
    const pending = readStash();
    if (pending.some(c => c.token === token)) return;  // same tap, replayed
    pending.push({ token, stashedAt: Date.now() });
    writeStash(pending);
};

/**
 * Capture a click token from the URL (`?nt=…`) on app entry.
 *
 * Covers the paths the native tap listener doesn't: web/PWA (there is no service
 * worker, so a web notification tap only ever arrives as a URL) and deep links.
 * Call on every load, before auth resolves.
 */
export const captureNotificationClickFromUrl = (): void => {
    try {
        const token = new URLSearchParams(window.location.search).get('nt');
        if (token) stashNotificationClick(token);
    } catch {
        // Non-browser context; nothing to capture.
    }
};

/**
 * Send any stashed click tokens. Idempotent server-side (first click wins), so a
 * double flush is harmless. Tokens are dropped from the stash once the server has
 * seen them — including when it reports the token unknown or already clicked,
 * since retrying those would never succeed.
 */
export const flushNotificationClicks = async (): Promise<void> => {
    const pending = readStash();
    if (pending.length === 0) {
        // readStash drops expired and malformed entries, but only from what it
        // returns — clear them out of storage too, or they sit there forever.
        writeStash([]);
        return;
    }

    const record = httpsCallable<{ token: string }, RecordClickResponse>(
        functions,
        'enough_record_notification_click',
    );

    const unresolved: PendingClick[] = [];
    for (const click of pending) {
        try {
            await record({ token: click.token });
        } catch {
            // Transient (offline, cold start). Keep it for the next flush.
            unresolved.push(click);
        }
    }
    writeStash(unresolved);
};
